import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import SmartImage from 'dpl/components/SmartImage';
import DogPlaceholderSvg from 'dpl/common/components/DogPlaceholderSvg';
import { isMobileUA, capitalize } from 'dpl/shared/utils';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import { BREEDER_REFUND_POLICY } from 'dpl/breeder_account/utils/constants';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import Icon from 'dpl/common/components/Icon';

import { ensureCurrencyIsPresent } from './util';

const isMobile = isMobileUA();
export default function StripeChargeModalPuppyCard({
  dog,
  amountInDollars,
  paymentCategory,
  breederProfile
}) {
  const photoDimensions = isMobile ? '40px' : '48px';
  const totalWithDollar = ensureCurrencyIsPresent(amountInDollars);
  const breedName = dog?.breed?.name;
  const isDeposit = paymentCategory === PAYMENT_REQUEST_CATEGORIES.DEPOSIT;
  const paymentText = !dog && isDeposit ? 'Deposit' : 'Payment';
  const titleText = `${paymentText} for ${dog?.name ?? 'a puppy'}`;

  const hasDeposit = isDeposit && breederProfile?.deposit_refund_policy;

  return (
    <div className="StripeChargeModalPuppyCard flex mh20-md mh6 ba b--light-gray br1 pv4 ph4">
      {dog?.profile_photo_url ? (
        <SmartImage
          src={dog?.profile_photo_url}
          width={photoDimensions}
          height={photoDimensions}
          className="br-100 mr3 bg-white"
          loadingClass="bg-near-white"
          crop
        />
      ) : (
        <DogPlaceholderSvg
          className="br-100 mr3 bg-white"
          crop
          width={photoDimensions}
          height={photoDimensions}
          icon
        />
      )}
      <div
        className={classnames('flex flex-auto pr1 f2 f3-md', {
          'items-center': !breedName && !hasDeposit,
          'flex-column': breedName || hasDeposit
        })}
      >
        <p>{titleText}</p>
        {isDeposit
          ? breederProfile.deposit_refund_policy && (
              <DepositPolicyDescription
                refundPolicy={breederProfile.deposit_refund_policy}
                depositDescription={breederProfile.deposit_description}
              />
            )
          : breedName && <p className="black o-50">{breedName} puppy</p>}
      </div>
      <div
        className={classnames('f2 f3-md', { 'flex items-center ': !breedName })}
      >
        {totalWithDollar}
      </div>
    </div>
  );
}

StripeChargeModalPuppyCard.propTypes = {
  breederProfile: PropTypes.shape({
    profile_photo_url: PropTypes.string,
    name: PropTypes.string.isRequired,
    pay_url: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_refund_policy: PropTypes.string,
    deposit_description: PropTypes.string
  }).isRequired,
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dog: PropTypes.shape({
    name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string,
    breed: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  paymentCategory: PropTypes.oneOf(Object.values(PAYMENT_REQUEST_CATEGORIES))
};

StripeChargeModalPuppyCard.defaultProps = {
  dog: null,
  amountInDollars: null,
  paymentCategory: PAYMENT_REQUEST_CATEGORIES.DOG_PAYMENT
};

function DepositPolicyDescription({ refundPolicy, depositDescription }) {
  return (
    <div className="flex align-items-center">
      <p className="black o-50 mr1">
        {refundPolicy === BREEDER_REFUND_POLICY.OTHER
          ? 'Deposit policy'
          : capitalize(refundPolicy)}
      </p>
      {depositDescription && (
        <TooltipWrapper title={depositDescription} minWidth="200px">
          <Icon name="info-dark" />
        </TooltipWrapper>
      )}
    </div>
  );
}

DepositPolicyDescription.propTypes = {
  refundPolicy: PropTypes.string.isRequired,
  depositDescription: PropTypes.string
};

DepositPolicyDescription.defaultProps = {
  depositDescription: null
};
